import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Loader2, Ticket } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import api from '../api/client';

export default function VoucherVerifyPage() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatMoney, formatDate } = useCurrency();
  const sig = searchParams.get('sig') || '';
  const [status, setStatus] = useState('loading'); // loading, valid, invalid, error
  const [voucher, setVoucher] = useState(null);

  useEffect(() => {
    if (!code) { setStatus('invalid'); return; }
    const verify = async () => {
      try {
        const res = await api.get(`/api/shop/verify/${code}?sig=${encodeURIComponent(sig)}`);
        if (res.data.valid || res.data.status) {
          setVoucher(res.data);
          setStatus(res.data.valid ? 'valid' : 'invalid');
        } else {
          setStatus('invalid');
        }
      } catch (err) {
        const detail = err.displayMessage || '';
        if (detail) {
          setVoucher({ error: detail });
          setStatus('invalid');
        } else {
          setStatus('error');
        }
      }
    };
    verify();
  }, [code, sig]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D1210] to-[#1a0b09] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="verify-page">
        {/* Header */}
        <div className="bg-[#561D1A] px-6 py-5 text-center">
          <h1 className="text-[#D4AF37] text-xl font-bold tracking-wide">LuxeMall</h1>
          <p className="text-white/60 text-xs mt-1">Gift Voucher Verification</p>
        </div>

        <div className="p-6">
          {status === 'loading' && (
            <div className="text-center py-8" data-testid="verify-loading">
              <Loader2 size={40} className="mx-auto mb-3 text-[#561D1A] animate-spin" />
              <p className="text-sm text-gray-500">Verifying voucher...</p>
            </div>
          )}

          {status === 'valid' && voucher && (
            <div className="space-y-4" data-testid="verify-valid">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ShieldCheck size={32} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-800">Valid Voucher</h2>
                <p className="text-xs text-gray-500 mt-1">This voucher is authentic and active</p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500 uppercase">Code</span>
                  <span className="text-sm font-mono font-bold text-gray-800">{voucher.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500 uppercase">Balance</span>
                  <span className="text-sm font-bold text-emerald-600">{formatMoney(voucher.balance)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500 uppercase">Type</span>
                  <span className="text-sm text-gray-700">{voucher.voucher_type}</span>
                </div>
                {voucher.expiry_date && (
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500 uppercase">Expires</span>
                    <span className="text-sm text-gray-700">{formatDate(voucher.expiry_date)}</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 text-center">Present this to a merchant for redemption via the Merchant Portal.</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center py-6" data-testid="verify-invalid">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle size={32} className="text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-800">Voucher Issue</h2>
              <p className="text-sm text-red-600 mt-2">{voucher?.error || 'This voucher code is invalid or has been tampered with.'}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-6" data-testid="verify-error">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle size={32} className="text-amber-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-800">Verification Unavailable</h2>
              <p className="text-sm text-gray-500 mt-2">Unable to verify at this time. Please try again or visit a participating store.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 text-center">
          <button onClick={() => navigate('/')} className="text-sm text-[#561D1A] hover:text-[#EC1A23] font-medium">
            Go to LuxeMall
          </button>
        </div>
      </div>
    </div>
  );
}
